import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import authStore from '@/stores/auth.store'
import { DomStorage } from '@/enums'
import appStore from '@/stores/app.store'
import ApiError from '@/types/ApiError'
import JobOpeningType from '@/units/b2b/types/JobOpeningType'
import PublicCvGeneratorSendData from '@/units/ds/types/PublicCvGeneratorSendData'
import Metadata from '@/types/Metadata'
import { datadogLogs } from '@datadog/browser-logs'
import accountStore from '@/stores/account.store'
import { Campus, CampusInterestResponse } from '@/modules/wizard/types/Campus'
import { MainscreenJourneyResponse } from '@/units/ds/types/MainscreenJourneyResponse.interface'
import Mobility from '@/types/Mobility.interface'

const apiClient: AxiosInstance = axios.create({
  baseURL:
    import.meta.env.VITE_API_BASE_URL +
    (window.location.href.includes(
      import.meta.env.VITE_B2B_BASE_URL as string,
    ) ||
    window.location.href.includes(
      import.meta.env.VITE_B2B_BASE_URL_FALLBACK as string,
    )
      ? '/b2b/v1'
      : '/v1'),
  withCredentials: true,
})

const applicationService: AxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_APPLICATION_SERVICE_BASE_URL,
  withCredentials: false,
})

/** Add headers and authorization for application service **/
// @ts-expect-error - TODO: fix this
applicationService.interceptors.request.use((config: AxiosRequestConfig) => {
  config.headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json',
    'Accept-Language': 'de', // language to "de", otherwise it will depending on users browser
  }
  return config
})

export type ApplicationServiceOption = {
  name: string
  value: string
}

const applicationServiceCatalog = {
  getLocations: (): Promise<AxiosResponse<ApplicationServiceOption[]>> =>
    applicationService.get('/locations?filter[units]=ds'),

  getPrograms: (
    locationCareId: string,
  ): Promise<AxiosResponse<ApplicationServiceOption[]>> =>
    applicationService.get(
      `/programs?filter[units]=ds&filter[languages]=de&filter[locations]=${locationCareId}`,
    ),

  getStudyStarts: (
    programCareId: string,
    locationCareId: string,
  ): Promise<AxiosResponse<ApplicationServiceOption[]>> =>
    applicationService.get(
      `/study-starts?filter[units]=ds&filter[programs]=${programCareId}&filter[locations]=${locationCareId}`,
    ),
}

/** Add headers and authorization for our backend **/
// @ts-expect-error - TODO: fix this
apiClient.interceptors.request.use((config: AxiosRequestConfig) => {
  let contentType = 'application/json'

  if (config.url === '/documents/upload') {
    contentType = 'multipart/form-data'
  }

  config.headers = {
    Accept: 'application/json',
    'Content-Type': contentType,
    Authorization: authStore.state.token
      ? 'Bearer ' + authStore.state.token
      : '',
  }

  return config
})

/** fetch errors and auth-problems (redirect to login) **/
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    if (response.data.cookie) {
      document.cookie =
        '_ga=' +
        response.data.cookie.code +
        '; path=/; expires=' +
        response.data.cookie.expiresAt +
        ';'
    }

    return response
  },
  async (error: any) => {
    const errorObject = JSON.parse(JSON.stringify(error))
    const customErrors = error.response?.data?.errors

    const errorStatusCode = errorObject.status

    datadogLogs.logger.error(
      '[ApiService] API Response Error',
      { uid: accountStore.state.account.uuid },
      error,
    )

    if (
      (errorStatusCode === 503 || errorObject.code === 'ERR_NETWORK') &&
      !window.location.href.includes('maintenance') &&
      !error.config?.url?.includes('upload')
    ) {
      console.debug('Downtime detected - Error:', error)
      console.debug('Downtime detected - Config:', error.config)
      console.debug('Downtime detected - URL:', error.config?.url)
      window.location.href = '/maintenance'
      return
    }

    if ([401, 403, 404].includes(errorStatusCode)) {
      const backendErrorCode = error.response?.data?.error?.code
      console.log('backendErrorCode!', backendErrorCode)
      if (backendErrorCode) {
        // Unauth-Request -> Redirect to auth login page
        if ([1000, 1003, 1004].includes(backendErrorCode)) {
          // UNAUTHENTICATED -> Go to Login, but save current path
          const path =
            window.location.pathname +
            window.location.search +
            window.location.hash
          sessionStorage.setItem(DomStorage.ORIGIN_URL_PATH, path)
          await appStore.redirectToLogin()
        }
        if (backendErrorCode === 1002) {
          await appStore.redirectToLogin()
        }
        appStore.setApiError(
          backendErrorCode ?? errorObject.status,
          errorObject.response?.data?.error?.message ?? errorObject.message,
        )
      }
      return Promise.reject({
        message: errorObject.message ?? 'Undefined request error',
        code: backendErrorCode ?? errorObject.status,
        customErrors: customErrors,
      } as ApiError)
    }

    return Promise.reject({
      message:
        error.response?.data?.message ??
        errorObject.message ??
        'Undefined request error',
      code: errorObject.status,
      customErrors: customErrors,
    } as ApiError)
  },
)

/** Return list of available endpoints **/
export const account = {
  get: (): Promise<any> => apiClient.get('/account'),
  getReferralLink: (): Promise<any> => apiClient.get('/account/referral-link'),
  patch: (params: object): Promise<any> => apiClient.patch('/account', params),
  postFeedback: (params: object) =>
    apiClient.post('/feedback', {
      ...params,
      agent: navigator?.userAgent,
      screenWidth: window?.innerWidth,
      screenHeight: window?.innerHeight,
    }),
  sendMailVerification: () => apiClient.post('/account/email-verification'),
  putMetadata: (metadata: Metadata): Promise<any> =>
    apiClient.put('/metadata', { ...metadata }),
  setOppyLost: (params: object) => apiClient.patch('/account/lost', params),
  putConsents: (
    consents: Array<{ type: string; hasOptedIn: boolean }>,
  ): Promise<any> => apiClient.put('/consents', consents),
  putLanguageSkills: (params: object): Promise<any> =>
    apiClient.put('/language-skills', params),
}

const unit = {
  getUnit: (): Promise<any> => apiClient.get('/unit'),
}

const experimentAssignments = {
  store: (
    assignments: Array<{
      campaignId: string
      variationId: number
      visitorId: string
    }>,
  ): Promise<any> => apiClient.post(`/experiment-assignments`, assignments),
}

const documents = {
  get: (): Promise<any> => apiClient.get('/documents'),
  getB2b: (companyId: number | null): Promise<any> =>
    apiClient.get(`/documents/${companyId}`),
  delete: (uuid: string): Promise<any> =>
    apiClient.delete('/documents/' + uuid),
  patch: (uuid: string, category: string, type?: string): Promise<any> =>
    apiClient.patch('/documents/' + uuid, { category: category, type: type }),
  post: (fileObj: FormData, onUploadProgress: (progress: number) => void) => {
    return apiClient.post('/documents/upload', fileObj, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'multipart/form-data',
        Authorization: authStore.state.token
          ? 'Bearer ' + authStore.state.token
          : '',
      },
      onUploadProgress: (progressEvent: any) => {
        const progress = Math.trunc(
          Math.round((progressEvent.loaded * 100) / progressEvent.total),
        )
        onUploadProgress(progress)
      },
    })
  },
  post64: (fileObj: object, onUploadProgress: (progress: number) => void) => {
    return apiClient.post('/documents/upload?base64=true', fileObj, {
      onUploadProgress: (progressEvent: any) => {
        const progress = Math.trunc(
          Math.round((progressEvent.loaded * 100) / progressEvent.total),
        )
        onUploadProgress(progress)
      },
    })
  },
  preview: (uuid: string): Promise<any> =>
    apiClient.get('/documents/' + uuid + '/preview', { responseType: 'blob' }),
}

const resources = {
  getLanguages: (): Promise<any> => apiClient.get('/languages'),
  getPdfFromHtml: (cat: string, html: string): Promise<any> =>
    apiClient.post(
      '/print-templates/upload',
      { type: cat, html: html },
      {
        responseType: 'blob',
      },
    ),
  createApplicantPortfolio: (): Promise<any> =>
    apiClient.post('/application-portfolio'),
  getJobOpenings: (page?: number): Promise<any> =>
    apiClient.get(`/job-openings?page=${page ?? 1}`),
  getInteractedJobOpenings: (): Promise<any> =>
    apiClient.get(`/job-openings/interacted`),
  getInvitationsJobOpenings: (): Promise<any> =>
    apiClient.get(
      `${import.meta.env.VITE_API_BASE_URL}/v2/job-openings/invitations`,
    ),
  getApplicationsJobOpenings: (): Promise<any> =>
    apiClient.get(
      `${import.meta.env.VITE_API_BASE_URL}/v2/job-openings/applications`,
    ),
  getJobOpeningDetails: (uuid: string): Promise<any> =>
    apiClient.get(`/job-openings/${uuid}`),
  setJobOpeningInterest: (uuid: string): Promise<any> =>
    apiClient.post(`/job-openings/${uuid}/express-interest`),
  setJobOpeningDisinterest: (uuid: string): Promise<any> =>
    apiClient.post(`/job-openings/${uuid}/express-disinterest`),
  favoriteJobOpening: (uuid: string): Promise<any> =>
    apiClient.post(`/job-openings/${uuid}/favorite`),
  acceptJobOpeningOffer: (uuid: string): Promise<any> =>
    apiClient.post(`/job-openings/${uuid}/accept-offer`),
  rejectJobOpeningOffer: (uuid: string): Promise<any> =>
    apiClient.post(`/job-openings/${uuid}/reject-offer`),
  getCampaigns: (): Promise<any> => apiClient.get(`/campaigns`),
  registerForCampaigns: (uuid: string): Promise<any> =>
    apiClient.post(`/campaigns/${uuid}/register`),
  participateCampaign: (uuid: string): Promise<any> =>
    apiClient.post(`/campaigns/${uuid}/participate`),
  getNotificationSettings: (): Promise<any> =>
    apiClient.get('/notification-settings'),
  updateNotificationSettings: (params: object): Promise<any> =>
    apiClient.put(`/notification-settings`, params),
  getCampusInterestData: (): Promise<AxiosResponse<CampusInterestResponse>> =>
    apiClient.get('/campus-interests'),
  getCampusOptions: (): Promise<AxiosResponse<{ campuses: Campus[] }>> =>
    apiClient.get('/campuses'),
  putCampusInterestData: (params: object): Promise<any> =>
    apiClient.put('/campus-interests', params),
  getNearestCampuses: (
    campusId: string,
    limit: number = 10,
    radiusKm: number = 200,
  ): Promise<any> =>
    apiClient.get(`/distance/nearest-campus/${campusId}`, {
      params: { limit, radius_km: radiusKm },
    }),
}

// Mobility
const mobility = {
  get: (): Promise<AxiosResponse<{ mobility: Mobility }>> =>
    apiClient.get('/mobility'),
  patch: (
    params: Partial<Mobility>,
  ): Promise<AxiosResponse<{ mobility: Mobility }>> =>
    apiClient.patch('/mobility', params),
}

// Wizard Flows
const wizardFlows = {
  get: (): Promise<any> => apiClient.get('/wizard-flows'),
  complete: (flow: string): Promise<any> =>
    apiClient.post('/wizard-flows/complete', { flow }),
}

// Matching Profile
const matchingProfile = {
  getQuestionsByCourses: (courseIds: string[]): Promise<any> => {
    const params = courseIds.map((id) => `courses[]=${id}`).join('&')
    return apiClient.get(`/questions?${params}`)
  },
  getCourses: (clusterIds?: number[]): Promise<any> => {
    if (clusterIds?.length) {
      const params = clusterIds.map((id) => `clusters[]=${id}`).join('&')
      return apiClient.get(`/courses?${params}`)
    }
    return apiClient.get('/courses')
  },
  getCourseInterests: (): Promise<any> => apiClient.get('/course-interests'),
  saveCourseInterests: (courseIds: string[]): Promise<any> =>
    apiClient.post('/course-interests', { courseIds }),
}

// Question Answers
const questionAnswers = {
  get: (): Promise<any> => apiClient.get('/question-answers'),
  save: (answers: Record<string, string[]>): Promise<any> =>
    apiClient.post('/question-answers', answers),
}

// Intakes
const intakes = {
  get: (dateFrom?: string, dateTo?: string): Promise<any> => {
    const params = new URLSearchParams()
    if (dateFrom) params.set('date_from', dateFrom)
    if (dateTo) params.set('date_to', dateTo)
    const query = params.toString()
    return apiClient.get(`/intakes${query ? `?${query}` : ''}`)
  },
}

// Intake Interests
const intakeInterests = {
  get: (): Promise<any> => apiClient.get('/intake-interests'),
  put: (dates: string[]): Promise<any> =>
    apiClient.put('/intake-interests', { dates }),
}

const general = {
  health: (): Promise<any> =>
    apiClient.get(import.meta.env.VITE_API_BASE_URL + '/health'),
  getMainscreen: (): Promise<AxiosResponse<MainscreenJourneyResponse>> =>
    apiClient.get('/mainscreen'),
  sendCvData: (cvData: PublicCvGeneratorSendData): Promise<any> =>
    apiClient.post('/cv-generator/send/upload', cvData),
  cvGeneratorPdf: (cat: string, html: string): Promise<any> =>
    apiClient.post(
      '/cv-generator/upload',
      { type: cat, html: html },
      {
        responseType: 'blob',
      },
    ),
  unsubscribeNewsletter: (tid: string): Promise<any> =>
    apiClient.post(`/unsubscribe/${tid}`),
  sendTrackingId: (tid: string): Promise<any> =>
    apiClient.post(`/t/${tid}`, {}),
}

// User endpoints (public, no auth required)
const users = {
  getByExternalUuid: (externalUuid: string): Promise<any> =>
    apiClient.get(`/users/${externalUuid}`),
  register: (
    uuid: string,
    password: string,
    passwordConfirmation: string,
  ): Promise<any> =>
    apiClient.post('/users/register', {
      uuid,
      password,
      passwordConfirmation,
    }),
}

export type ApplicationV2Payload = {
  form: {
    formkey: string
    entryFormular: string
    salesforceId?: string | null
    applicationId: string
    businessUnit: string
  }
  profile: {
    id: string
    email: string
    firstName: string
    lastName: string
    street: string
    postalCode: string
    city: string
    countryCode?: string | null
    dateOfBirth?: string | null
    phone1?: string | null
  }
  campus: {
    careId: string
    name?: string | null
    phone?: string | null
    email?: string | null
    street?: string | null
    zipCode?: string | null
    city?: string | null
  }
  course: {
    intake: string
    productId: string
    name?: string | null
    duration?: number | null
    isPrimary?: boolean | null
    family?: string | null
  }
  recommender?: {
    name?: string | null
    email?: string | null
  }
}

export type ApplicationV2Response = {
  redirect_url: string
  expires_in?: number
  token_type?: string
  requires_manual_login?: boolean
}

export type ApplicationRouteAccessResponse = {
  access: boolean
}

const application = {
  verifyRouteAccess: (
    password: string,
  ): Promise<AxiosResponse<ApplicationRouteAccessResponse>> =>
    axios.post(
      `${import.meta.env.VITE_API_BASE_URL}/v2/application-route/access`,
      { password },
      {
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/json',
        },
      },
    ),

  store: (
    payload: ApplicationV2Payload,
  ): Promise<AxiosResponse<ApplicationV2Response>> =>
    axios.post(`${import.meta.env.VITE_API_BASE_URL}/v2/application`, payload, {
      headers: {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
    }),
}

// B2B Endpoints
const b2b = {
  getUser: (): Promise<any> => apiClient.get('/me'),
  putConsents: (
    consents: Array<{ type: string; hasOptedIn: boolean }>,
  ): Promise<any> => apiClient.put('/consents', consents),
  patchUser: (params: object): Promise<any> => apiClient.patch(`/me`, params),
  setActiveCompany: (companyId: number): Promise<any> =>
    apiClient.post(`/me/companies/${companyId}/set-default`),
  putMetadata: (metadata: Metadata): Promise<any> =>
    apiClient.put('/metadata', { ...metadata }),

  getCompany: (companyId: string | number): Promise<any> =>
    apiClient.get(`/companies/${companyId}`),
  patchCompanyMasterData: (
    companyId: string | number,
    params: object,
  ): Promise<any> => apiClient.patch(`/companies/${companyId}`, params),
  patchCompanyPortrait: (
    companyId: string | number,
    params: object,
  ): Promise<any> =>
    apiClient.patch(`/companies/${companyId}/portrait`, params),
  patchCompanyPaymentDetails: (
    companyId: string | number,
    params: object,
  ): Promise<any> =>
    apiClient.patch(`/companies/${companyId}/payment-details`, params),
  patchCompanyPaymentType: (
    companyId: string | number,
    params: object,
  ): Promise<any> =>
    apiClient.patch(`/companies/${companyId}/payment-type`, params),
  acceptCompanyInvitation: (companyId: string | number): Promise<any> =>
    apiClient.post(`/me/companies/${companyId}/accept-invitation`),
  declineCompanyInvitation: (companyId: string | number): Promise<any> =>
    apiClient.post(`/me/companies/${companyId}/decline-invitation`),

  getBankDetailsByIban: (iban: string): Promise<any> =>
    applicationService.get(`/bank-info/${iban}`),
  getInvoices: (): Promise<any> => apiClient.get('/accounting/invoices'),
  getNotices: (): Promise<any> => apiClient.get('/accounting/notices'),
  getNotifications: (limit?: number, offset?: number): Promise<any> =>
    apiClient.get('/notifications', {
      params: { limit: limit ?? 4, offset: offset ?? 0 },
    }),

  setNotificationRead: (id: number): Promise<any> =>
    apiClient.patch(`/notifications/${id}/read`),
  setNotificationReadAll: (): Promise<any> =>
    apiClient.patch(`/notifications/read`),
  sendNotification: (params: object): Promise<any> =>
    apiClient.post(`/notifications`, params),

  getNotificationSettings: (): Promise<any> =>
    apiClient.get('/notification-settings'),
  updateNotificationSettings: (params: object): Promise<any> =>
    apiClient.put(`/notification-settings`, params),

  // Bedarfsmeldungen / JobOpenings
  // get list of bedarfsmeldungen/jobopenings in short form
  getCompanyJobOpeningsOverview: (companyId: number): Promise<any> =>
    apiClient.get(`/companies/${companyId}/job-openings/overview`),
  createCompanyJobOpeningsOverview: (
    companyId: number,
    postData: JobOpeningType,
  ): Promise<any> =>
    apiClient.post(`/companies/${companyId}/job-openings`, postData),
  patchCompanyJobOpeningsOverview: (
    companyId: number,
    jobOpeningId: number,
    patchData: JobOpeningType,
  ): Promise<any> =>
    apiClient.patch(
      `/companies/${companyId}/job-openings/${jobOpeningId}`,
      patchData,
    ),
  getCompanyJobOpeningDetails: (
    companyId: number,
    jobOpeningId: string,
  ): Promise<any> =>
    apiClient.get(`/companies/${companyId}/job-openings/${jobOpeningId}`),
  deleteCompanyJobOpeningDetails: (
    companyId: number,
    jobOpeningId: string,
  ): Promise<any> =>
    apiClient.delete(`/companies/${companyId}/job-openings/${jobOpeningId}`),

  arrangeInterviewWithJobApplicant: (
    jobApplicantUuid: string,
    interviewDateSuggestions: string,
  ): Promise<any> =>
    apiClient.post(`/applicants/${jobApplicantUuid}/arrange-interview`, {
      interviewDateSuggestions: interviewDateSuggestions,
    }),
  rejectJobApplicant: (
    jobApplicantUuid: string,
    feedbackText: string,
  ): Promise<any> =>
    apiClient.post(`/applicants/${jobApplicantUuid}/reject`, { feedbackText }),
  acceptJobApplicant: (
    jobApplicantUuid: string,
    mismatchModel: object,
  ): Promise<any> =>
    apiClient.post(`/applicants/${jobApplicantUuid}/accept`, mismatchModel),

  // TODO: define searchData type
  searchCompanyJobOpeningApplicants: (
    companyId: number,
    jobOpeningId: string,
    searchData: object,
  ): Promise<any> =>
    apiClient.post(
      `/companies/${companyId}/job-openings/${jobOpeningId}/applicants`,
      searchData,
    ),

  getApplicant: (
    companyId: number | string,
    jobOpeningId: number | string,
    applicantId: number | string,
  ): Promise<any> =>
    apiClient.get(
      `/companies/${companyId}/job-openings/${jobOpeningId}/applicants/${applicantId}`,
    ),
  getApplicantContract: (
    companyId: number,
    jobOpeningId: number | string,
    applicantId: number | string,
  ): Promise<any> =>
    apiClient.get(
      `/companies/${companyId}/job-openings/${jobOpeningId}/applicants/${applicantId}/contract`,
    ),
  upsertApplicantContract: (
    companyId: number,
    jobOpeningId: number | string,
    applicantId: number | string,
    data: object,
  ): Promise<any> =>
    apiClient.post(
      `/companies/${companyId}/job-openings/${jobOpeningId}/applicants/${applicantId}/contract`,
      data,
    ),

  getApplicantComments: (
    companyId: number | string,
    jobOpeningId: number | string,
    applicantId: number | string,
  ): Promise<any> =>
    apiClient.get(
      `/companies/${companyId}/job-openings/${jobOpeningId}/applicants/${applicantId}/comments`,
    ),
  createApplicantComment: (
    companyId: number | string,
    jobOpeningId: number | string,
    applicantId: number | string,
    data: object,
  ): Promise<any> =>
    apiClient.post(
      `/companies/${companyId}/job-openings/${jobOpeningId}/applicants/${applicantId}/comment`,
      data,
    ),
  deleteApplicantComment: (
    companyId: number | string,
    jobOpeningId: number | string,
    applicantId: number | string,
    commentId: number | string,
  ): Promise<any> =>
    apiClient.delete(
      `/companies/${companyId}/job-openings/${jobOpeningId}/applicants/${applicantId}/comment/${commentId}`,
    ),
  patchApplicantComment: (
    companyId: number | string,
    jobOpeningId: number | string,
    applicantId: number | string,
    data: object,
  ): Promise<any> =>
    apiClient.patch(
      `/companies/${companyId}/job-openings/${jobOpeningId}/applicants/${applicantId}/comment`,
      data,
    ),

  getCompanyEmployees: (companyId: number): Promise<any> =>
    apiClient.get(`/companies/${companyId}/employees`),
  getCompanyEmployee: (companyId: number, employeeId: number): Promise<any> =>
    apiClient.get(`/companies/${companyId}/employees/${employeeId}`),
  createCompanyEmployee: (companyId: number, params: object): Promise<any> =>
    apiClient.post(`/companies/${companyId}/employees`, params),
  editCompanyEmployee: (
    companyId: number,
    employeeId: number,
    params: object,
  ): Promise<any> =>
    apiClient.patch(`/companies/${companyId}/employees/${employeeId}`, params),
  resendCompanyEmployeeInvite: (
    companyId: number,
    employeeId: number,
  ): Promise<any> =>
    apiClient.post(
      `/companies/${companyId}/employees/${employeeId}/resend-invitation`,
    ),
  deleteCompanyEmployee: (
    companyId: number,
    employeeId: number,
  ): Promise<any> =>
    apiClient.delete(`/companies/${companyId}/employees/${employeeId}`),

  postFeedback: (params: object): Promise<any> =>
    apiClient.post('/feedback', {
      ...params,
      agent: navigator?.userAgent,
      screenWidth: window?.innerWidth,
      screenHeight: window?.innerHeight,
    }),
}

export default {
  account,
  unit,
  experimentAssignments,
  documents,
  resources,
  apiClient,
  b2b,
  applicationService,
  applicationServiceCatalog,
  mobility,
  wizardFlows,
  matchingProfile,
  questionAnswers,
  intakes,
  intakeInterests,
  general,
  users,
  application,
}
